import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Filter from "./FilterBranch";
import AddBranch from "./AddBranch";
import Delete from "./Delete";
import { downloadCSV, fetchBranch } from "services/branch";
import { FormButton } from "components/Common/Buttons";
import Modal from "components/Common/Modal";
import { COLUMNS_DATA, FILTER_DEFINATIONS } from "./config";
import Table from "components/Common/Table/index";
import usePagination from "hooks/usePagination";
import useFilterHook from "hooks/useFilter";
import { DEFAULT_TOTAL_RECORDS, MODALS, STALE_TIME } from "utils/constants";
import useDownloadCSV from "hooks/useDownloadCSV";
import Loader from "components/Common/loader";
import { fetchEmployeeTargets } from "services/employeeTarget";

const EmployeeTargets = () => {
  const [isAddBranchOpen, setIsAddBranchOpen] = useState(false);
  const [rowDataForEdit, setRowDataForEdit] = useState(null);
  const [isDelete, setIsDelete] = useState(-1);
  const [isSuperFranchise] = useState(() =>
    Number(localStorage.getItem("is_super_franchise")),
  );
  const [isFranchiseEmployee] = useState(() =>
    Number(localStorage.getItem("is_franchise_employee")),
  );
  const [isSubFranchise] = useState(() =>
    Number(localStorage.getItem("sub_franchise")),
  );

  const {
    currentPage,
    itemPerPage,
    disablePrev,
    disableNext,
    disableFirst,
    disableLast,
    totalPages,
    updateCurrentPage,
    updateTotalRecords,
  } = usePagination();

  const DownloadButton = useDownloadCSV(downloadCSV, "branch.csv");

  const { filters, updateFilter, updateMultipleFilters } = useFilterHook(
    FILTER_DEFINATIONS,
    updateCurrentPage,
  );

  const editBranch = useCallback((row) => {
    setRowDataForEdit(row);
    setIsAddBranchOpen(true);
  }, []);

  const deleteRow = useCallback((row) => {
    setIsDelete(row.id);
  }, []);

  const {
    data: branchData,
    isLoading,
    refetch: refetchBranches,
  } = useQuery(
    ["fetch-employee-targets", currentPage, filters],
    () => fetchEmployeeTargets(currentPage, filters),
    {
      keepPreviousData: false,
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res?.data?.additional_data?.total_count);
      },
      select: (res) => {
        const response = res?.data?.employee_target_data;
        if (response && response.length) {
          res.data.employee_target_data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching branches:", res);
      },
    },
  );

  const onClose = useCallback(
    (refetch = false) => {
      if (refetch) refetchBranches();
      setIsAddBranchOpen(false);
      setRowDataForEdit(null);
    },
    [refetchBranches],
  );

  const onCloseDelete = useCallback(
    (refetch = false) => {
      if (refetch) refetchBranches();
      setIsDelete(-1);
    },
    [refetchBranches],
  );

  const columns = useMemo(() => {
    return COLUMNS_DATA({
      deleteRow,
      editBranch,
      isSuperFranchise,
      isFranchiseEmployee,
      isSubFranchise,
      filters,
    });
  }, [
    deleteRow,
    editBranch,
    isSuperFranchise,
    isFranchiseEmployee,
    isSubFranchise,
    filters.is_active,
  ]);

  useEffect(() => {
    if (filters.text) {
      refetchBranches();
    }
  }, [filters.text, refetchBranches]);

  const loadNextPage = useCallback(() => {
    updateCurrentPage(currentPage + 1);
  }, [currentPage, updateCurrentPage]);

  const loadPreviousPage = useCallback(() => {
    updateCurrentPage(currentPage - 1);
  }, [currentPage, updateCurrentPage]);

  const loadFirstPage = useCallback(() => {
    if (currentPage !== 1) {
      updateCurrentPage(1);
    }
  }, [currentPage, updateCurrentPage]);

  const loadLastPage = useCallback(() => {
    if (currentPage !== totalPages) {
      updateCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, updateCurrentPage]);

  if (isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  return (
    <div className="container-fluid mb-3">
      <div className="box mb-4">
        <div className="mb-2 boxHeader employee-targets-box-header">
          <h1 className="mb-0">Employee Targets</h1>
          <div className="employee-targets-toolbar">
            <div className="employee-targets-filter">
              <Filter
                updateFilter={updateFilter}
                filters={filters}
                updateMultipleFilters={updateMultipleFilters}
              />
            </div>
            <div className="employee-targets-actions">
              <FormButton
                onClick={() => setIsAddBranchOpen(true)}
                button_name="Add Employee Target"
              />
            </div>
          </div>
        </div>

        <Modal
          type="modal-lg"
          show={isAddBranchOpen}
          onHide={() => onClose(false)}
          modalName={MODALS.ADD_BRANCH_MODAL}
          title={
            rowDataForEdit ? "Edit Employee Target" : "Add Employee Target"
          }
          body={<AddBranch rowData={rowDataForEdit} onClose={onClose} />}
        />

        <Modal
          show={isDelete !== -1}
          onHide={() => onCloseDelete()}
          modalName={MODALS.DELETE_BRANCH_MODAL}
          title={"Are you sure you want to delete this employee target?"}
          footer={<Delete onClose={onCloseDelete} item={isDelete} />}
        />

        <div className="boxBody">
          <Table
            data={branchData?.data?.employee_target_data || []}
            isLoading={isLoading}
            tableColumns={columns}
            currentPage={currentPage}
            totalPages={totalPages}
            itemPerPage={itemPerPage}
            disablePrev={disablePrev}
            disableNext={disableNext}
            disableFirst={disableFirst}
            disableLast={disableLast}
            loadFirstPage={loadFirstPage}
            loadLastPage={loadLastPage}
            totalRecords={5}
            loadNextPage={loadNextPage}
            loadPreviousPage={loadPreviousPage}
          />
        </div>
      </div>
    </div>
  );
};

export default EmployeeTargets;
